/**
 * Quotes — `/sales/quotes`.
 *
 * A cursor list over the branch's quotes, newest first, with the one action
 * that belongs to the list rather than to a document: recording which quotes
 * have lapsed.
 *
 * A quote whose validity has passed is still `sent` until that sweep runs.
 * The list shows it as lapsed AND leaves the stored status alone, because the
 * alternative — showing "expired" for a row the database calls "sent" — makes
 * the screen and the record disagree about the same quote.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarX2, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, textCell } from "@/components/data-table";
import { MoneyDisplay } from "@/components/money/money-display";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { formatCalendarDate } from "@/lib/format";

import {
  QUOTE_STATUSES,
  expireQuotes,
  hasLapsed,
  type QuoteFilters,
  type QuoteSummary,
} from "../quotes-api";
import { useQuoteList } from "../queries";

const ALL = "__all__";

export interface QuotesPageProps {
  onOpenQuote: (quoteId: string) => void;
  onNewQuote: () => void;
  canWrite?: boolean;
  pageLimit?: number;
  /** Today, as the screen sees it — injected so a test is not a clock. */
  today?: string;
}

export function QuotesPage({
  onOpenQuote,
  onNewQuote,
  canWrite = false,
  pageLimit,
  today = new Date().toISOString().slice(0, 10),
}: QuotesPageProps) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const filters = useMemo<QuoteFilters>(() => {
    const built: QuoteFilters = {};
    if (status !== ALL) built.status = status as QuoteFilters["status"];
    return built;
  }, [status]);

  const query = useQuoteList(filters, pageLimit);

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const sweep = () => {
    setBusy(true);
    setOutcome(null);
    void expireQuotes(today, newIdempotencyKey())
      .then((result) => {
        setOutcome(
          t("sales.quotes.swept", "{{count}} quote(s) recorded as lapsed.", {
            count: result.expired_count,
          }),
        );
        query.refetch();
      })
      .catch(() => setOutcome(t("sales.quotes.sweepError", "The sweep could not run.")))
      .finally(() => setBusy(false));
  };

  const columns = useMemo<ColumnDef<QuoteSummary, unknown>[]>(
    () => [
      {
        id: "quote_number",
        accessorKey: "quote_number",
        header: t("sales.quotes.columns.number", "Quote"),
        cell: ({ row }) => (
          <span dir="ltr" className="font-mono text-xs">
            {row.original.quote_number}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("sales.quotes.columns.status", "Status"),
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1">
            <Badge variant={row.original.status === "converted" ? "success" : "secondary"}>
              {t(`sales.quoteStatus.${row.original.status}`, row.original.status)}
            </Badge>
            {hasLapsed(row.original, today) ? (
              <Badge variant="outline">{t("sales.quotes.lapsed", "Lapsed")}</Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: "valid_until",
        accessorKey: "valid_until",
        header: t("sales.quotes.columns.validUntil", "Valid until"),
        cell: ({ row }) => textCell(day(row.original.valid_until)),
      },
      {
        id: "grand_total",
        accessorKey: "grand_total",
        header: t("sales.quotes.columns.total", "Total"),
        meta: { numeric: true },
        cell: ({ row }) => (
          <MoneyDisplay
            value={{ amount: row.original.grand_total, currency: row.original.currency }}
          />
        ),
      },
    ],
    // `day` closes over the active language, which is what the cell renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, today],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("sales.quotes.title", "Quotes")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "sales.quotes.subtitle",
              "What has been offered and on what terms. A quote moves no stock and posts nothing until it becomes an order.",
            )}
          </p>
        </div>
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={sweep}>
              <CalendarX2 className="size-4" aria-hidden="true" />
              {t("sales.quotes.sweep", "Record what has lapsed")}
            </Button>
            <Button type="button" onClick={onNewQuote}>
              <Plus className="size-4" aria-hidden="true" />
              {t("sales.quotes.new", "New quote")}
            </Button>
          </div>
        ) : null}
      </div>

      {outcome === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {outcome}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="quote-status">{t("sales.quotes.columns.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="quote-status" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {QUOTE_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`sales.quoteStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenQuote(row.id)}
        emptyMessage={t("sales.quotes.empty", "No quote matches these filters.")}
      />
    </div>
  );
}
